import { type IDetectedBarcode, Scanner } from '@yudiel/react-qr-scanner'
import { CheckCircle2, Loader2, ScanLine, XCircle } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'react-toastify'
import { useCheckIn } from '@/api/queries/dds-attendance-queries'
import { CameraGate, CameraStatusLabel, PageHeader } from '@/components/commons'
import { DdsMoodPicker } from '@/components/dds-attendance'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/constants/routes'
import { useCameraPermission } from '@/hooks/use-camera-permission'
import type { DDSCheckInResult, DDSMood } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'
import { getCameraErrorMessage } from '@/utils/camera-error'
import {
	extractCheckInToken,
	getAttendanceErrorKey,
	getCheckInTokenParam,
} from '@/utils/dds-attendance'
import { formatDateToDisplay } from '@/utils/formatDate'

type CheckInState =
	| { status: 'scanning' }
	| { status: 'choosing'; token: string }
	| { status: 'submitting' }
	| { status: 'success'; result: DDSCheckInResult }
	| { status: 'error'; message: string }

export const DDSCheckInPage = () => {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const [searchParams, setSearchParams] = useSearchParams()
	const checkInMutation = useCheckIn()
	const [state, setState] = useState<CheckInState>({ status: 'scanning' })
	const [mood, setMood] = useState<DDSMood | null>(null)
	const camera = useCameraPermission()

	// O participante escolhe o humor antes de confirmar a presença
	const startChoosing = (token: string) => {
		setMood(null)
		setState({ status: 'choosing', token })
	}

	const submit = async (token: string, selectedMood: DDSMood) => {
		setState({ status: 'submitting' })

		try {
			const result = await checkInMutation.mutateAsync({
				token,
				mood: selectedMood,
			})
			setState({ status: 'success', result })
		} catch (error) {
			const key = getAttendanceErrorKey(error)
			setState({
				status: 'error',
				message: key ? t(key) : getApiErrorMessage(error),
			})
		}
	}

	// Link aberto pela câmera nativa: guarda o token, tira da URL e pede o humor
	const urlToken = getCheckInTokenParam(searchParams)
	// biome-ignore lint/correctness/useExhaustiveDependencies: dispara uma vez por token vindo da URL
	useEffect(() => {
		if (!urlToken) return
		setSearchParams({}, { replace: true })
		startChoosing(urlToken)
	}, [urlToken])

	const handleScan = (codes: IDetectedBarcode[]) => {
		const raw = codes[0]?.rawValue
		if (!raw || state.status !== 'scanning') return

		const token = extractCheckInToken(raw)
		if (!token) {
			toast.warning(t('ddsCheckIn.notAttendanceQr'))
			return
		}
		startChoosing(token)
	}

	const scanAgain = () => {
		setMood(null)
		setState({ status: 'scanning' })
	}

	return (
		<div className="mx-auto flex w-full max-w-md flex-col px-4 py-6">
			<PageHeader
				title={t('ddsCheckIn.title')}
				description={t('ddsCheckIn.subtitle')}
				onBack={() => navigate(ROUTES.DDS.HUB)}
			/>

			{state.status === 'scanning' && (
				<div className="flex flex-col items-center gap-4">
					{camera.state === 'granted' && (
						<CameraStatusLabel state={camera.state} />
					)}
					<CameraGate camera={camera}>
						<div className="aspect-square w-full max-w-sm overflow-hidden rounded-xl border">
							<Scanner
								constraints={{ facingMode: 'environment' }}
								onError={(error) =>
									toast.error(getCameraErrorMessage(error, t), {
										autoClose: 10000,
									})
								}
								onScan={handleScan}
								scanDelay={1000}
							/>
						</div>
					</CameraGate>
					<p className="flex items-center gap-2 text-center text-muted-foreground text-sm">
						<ScanLine className="size-4 shrink-0" />
						{t('ddsCheckIn.instructions')}
					</p>
				</div>
			)}

			{state.status === 'choosing' && (
				<div className="flex flex-col gap-5">
					<div className="text-center">
						<p className="font-bold text-kaefer-dark-grey text-lg">
							{t('ddsCheckIn.mood.question')}
						</p>
						<p className="text-muted-foreground text-sm">
							{t('ddsCheckIn.mood.hint')}
						</p>
					</div>
					<DdsMoodPicker onChange={setMood} value={mood} />
					<div className="flex flex-col gap-2 sm:flex-row-reverse">
						<Button
							className="w-full sm:flex-1"
							disabled={!mood}
							onClick={() => mood && submit(state.token, mood)}
							type="button"
						>
							<CheckCircle2 className="size-4" />
							{t('ddsCheckIn.mood.confirm')}
						</Button>
						<Button
							className="w-full sm:w-auto"
							onClick={scanAgain}
							type="button"
							variant="outline"
						>
							{t('ddsCheckIn.mood.cancel')}
						</Button>
					</div>
				</div>
			)}

			{state.status === 'submitting' && (
				<div className="flex flex-col items-center gap-3 py-16 text-muted-foreground">
					<Loader2 className="size-10 animate-spin" />
					<p>{t('ddsCheckIn.submitting')}</p>
				</div>
			)}

			{state.status === 'success' && (
				<div className="flex flex-col items-center gap-3 rounded-xl border border-kaefer-grass-green/40 bg-kaefer-grass-green/5 px-6 py-10 text-center">
					<CheckCircle2 className="size-14 text-kaefer-grass-green" />
					<p className="font-bold text-kaefer-dark-grey text-lg">
						{state.result.alreadyRegistered
							? t('ddsCheckIn.alreadyRegistered')
							: t('ddsCheckIn.success')}
					</p>
					<p className="font-medium text-kaefer-dark-grey">
						{state.result.subject}
					</p>
					<p className="text-muted-foreground text-sm">
						{formatDateToDisplay(state.result.date)}
					</p>
					<Button
						className="mt-4 w-full sm:w-auto"
						onClick={() => navigate(ROUTES.DASHBOARD.HOME)}
						type="button"
					>
						{t('ddsCheckIn.done')}
					</Button>
				</div>
			)}

			{state.status === 'error' && (
				<div className="flex flex-col items-center gap-3 rounded-xl border border-destructive/40 bg-destructive/5 px-6 py-10 text-center">
					<XCircle className="size-14 text-destructive" />
					<p className="font-semibold text-kaefer-dark-grey">{state.message}</p>
					<Button
						className="mt-4 w-full sm:w-auto"
						onClick={scanAgain}
						type="button"
					>
						<ScanLine className="size-4" />
						{t('ddsCheckIn.scanAgain')}
					</Button>
				</div>
			)}
		</div>
	)
}
